import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { BaseHttpService } from './base-http.service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from './config.service';
import { AuthenticationService } from './authentication.service';
import { Observable } from 'rxjs';

@Injectable()

export class CampaignsService extends BaseHttpService {

  private resource = 'campaigns';

  /**
   * Life cycle method
   * @param http HttpClient
   * @param configService ConfigService
   * @param authenticationService Authentication Service
   */
  constructor(
    public http: HttpClient,
    public configService: ConfigService,
    public authenticationService: AuthenticationService
  ) {
    super(http);
  }

  /**
   * Get campaigns
   * @param limit - number of campaigns to return
   * @param offset - number of campaigns to skip
   * @param contains - string to search for in the campaign name
   * @param tags - tags to filter by
   */
  getCampaigns(limit = 10, offset = 0, contains = '', tags = ''): Observable<any> {

    // console.log('limit and offset ', limit, offset);
    const params: any = {
      limit,
      offset,
      contains,
      loadCounts: true,
      tags
    };

    return this.get('campaign-list', '', params);
  }

  /**
   * Get all campaigns
   */
  getAllCampaigns(): Observable<any> {
    return this.get('all-campaigns', '');
  }

  getAllCampaignsTitles(startsWith = ''): Observable<any> {
    return this.get('campaign-list-to-select', '', { startsWith });
  }

  getCampaignLogSearchResults(ids, category, recipientEmail, searchText, isEmployeeCampaign, limit, searchAfter): Observable<any> {
    const resource = isEmployeeCampaign ? 'employee-campaign/searchLogs' : 'campaign/searchLogs';
    const params: any = {
      category,
      recipientEmail,
      limit
    };

    if (ids) { params.ids = ids; }
    if (searchAfter) { params.searchAfter = searchAfter; }
    if (searchText) { params.searchText = searchText; }

    return this.get(resource, undefined, params);
  }

  /**
   * Set campaign to not started
   * @param id - id of the campaign
   */
  setCampaignToNotStarted(id: string|number): Observable<any> {
    return this.post('campaign/set-campaign-to-not-started', {id});
  }

  /**
   * Push campaign to production
   * @param id - id of the campaign
   */
  pushCampaignToProduction(id: string|number): Observable<any> {
    return this.post(`campaign/push-production/${id}`, {id});
  }

  /**
   * POST request for getting matching records for campaign
   * @param campaign
   * @param nextToken
   */
  getMatchingRecords(campaign: any, nextToken = ''): Observable<any> {
    // console.log('campaign', campaign);
    if (campaign.employeeCampaignType) {
      return this.post('employee-campaign/matching-records', campaign);
    } else {
      console.log(campaign.tempNextToken);
      return this.post('campaign/matching-records', campaign,
        { nextToken: encodeURIComponent(nextToken), limit: 100 });
    }
  }

  /**
   * POST Request for adding campagin
   * @param campaign
   */
  addCampaign(campaign: any): Observable<any> {
    return this.post('campaign', campaign);
  }

  /**
   * Delete selected campaign with given id
   * @param id
   */
  deleteCampaign(id: string): Observable<any> {
    return this.delete('campaign', id);
  }

  getCampaignHistory(id: string, title: string, from: number, size: number): Observable<any> {
    const ep = `show-campaign-history/${id}`;
    const params = { title, from, size };
    return this.get(`${ep}`, '', params);
  }

  /**
   * Send test email
   * @param payload
   */
  sendTestEmail(payload: any): Observable<any> {
    const resource = payload.isEmployeeCampaign ? 'employee-campaign/email-test/0' : 'campaign/email-test/0';
    return this.post(resource, payload);
  }

  /**
   * Send Test SMS
   * @param payload
   */
  sendTestSMS(payload: any): Observable<any> {
    const resource = payload.isEmployeeCampaign ? 'employee-campaign/sms-test/0' : 'campaign/sms-test/0';
    return this.post(resource, payload);
  }

  /**
   * Send Test Slack
   * @param payload
   */
  sendTestSlack(payload: any): Observable<any> {
    const resource = payload.isEmployeeCampaign ? 'employee-campaign/slack-test/0' : 'campaign/slack-test/0';
    return this.post(resource, payload);
  }

  /**
   * Send Test Teams
   * @param payload
   */
  sendTestTeams(payload: any): Observable<any> {
    console.log(payload);
    const resource = payload.isEmployeeCampaign ? 'employee-campaign/teams-test/0' : 'campaign/teams-test/0';
    return this.post(resource, payload);
  }


  /**
   * Return logs
   * @param id campaign id
   * @param isEmployeeCampaign
   * @param nextToken
   */
  getCampaignLogsByEmail(email: string, isEmployeeCampaign: boolean, nextToken: string): Observable<any> {
    const resource = isEmployeeCampaign ? 'employee-campaign/logs-by-email' : 'campaign/logs-by-email';
    return this.get(resource, email, { limit: 50, nextToken });
  }

  /**
   * Return logs
   * @param id campaign id
   * @param isEmployeeCampaign
   * @param nextToken
   */
  getCampaignLogs(id: string, isEmployeeCampaign: boolean, nextToken: string): Observable<any> {
    const resource = isEmployeeCampaign ? 'employee-campaign/logs' : 'campaign/logs';
    return this.get(resource, id, { limit: 50, nextToken });
  }

  /**
   * Return logs
   * @param id campaign id
   * @param isEmployeeCampaign
   * @param nextToken
   */
  getCampaignEmailLogs(emailListItemId: string, isEmployeeCampaign: boolean, nextToken: string): Observable<any> {
    const resource = isEmployeeCampaign ? 'employee-campaign/logs-by-email' : 'campaign/logs-by-email';
    return this.get(resource, emailListItemId, { limit: 50, nextToken });
  }

  /**
   * Get campaign list names cache
   */
  getCampaignListNamesCache(): Observable<any> {
    return this.get( 'campaign-list-ids-names', undefined, {});
  }
  /**
   * Return logs
   * @param id campaign id
   * @param isEmployeeCampaign
   */
  getScheduledCampaigns(id: string, isEmployeeCampaign: boolean, nextToken: string): Observable<any> {
    const resource = isEmployeeCampaign ? 'employee-campaign/scheduled' : 'campaign/scheduled';
    if (nextToken.length > 0) {
      return this.get(resource, id, { nextToken: encodeURIComponent(nextToken) });
    } else {
      return this.get(resource, id, {});
    }
  }

  /**
   * Delete scheduled campaign
   * @param id - id of the campaign
   */
  deleteScheduled(id: string): Observable<any> {
    const resource = 'campaign/delete-scheduled';
    return this.delete(resource, id);
  }

  /**
   * Send scheduled campaign
   * @param id - id of the campaign
   */
  sendScheduled(id: string): Observable<any> {
    const resource = `campaign/send-scheduled/${id}`;
    return this.post(resource, { id });
  }
  
  /**
   * Return campaign object by campaign id
   * @param id id of campaign
   */
  getCampaignById(id: string): Observable<any> {
    return this.get('campaign', id);
  }

  /**
   * Update campaign
   * @param payload Campaign object
   * @param id campaign id
   */
  updateCampaignById(payload: any, id: string): Observable<any> {
    return this.put('campaign', payload, id);
  }

  /**
   * Get campaign suggestions from AI
   * @param customInstructions
   * @param currentEmailText
   * @param currentCss
   */
  getCampaignSuggestions(customInstructions: string, currentEmailText: string, currentCss: string): Observable<any> {
    return this.post('campaign-email-body-suggestions', { customInstructions, currentEmailText, currentCss });
  }
}
